import { HttpEvent, HttpHandlerFn, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface CachedResponse {
  body: unknown;
  status: number;
  statusText: string;
  headers: Record<string, string[]>;
}

const DB_NAME = 'cockpit-dev-cache';
const STORE_NAME = 'responses';
const DB_VERSION = 1;

const enabled = !environment.production && environment.cacheHttpGet;
const indexedDbAvailable = typeof indexedDB !== 'undefined';

function initDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

const dbPromise: Promise<IDBDatabase | null> =
  enabled && indexedDbAvailable
    ? initDb().catch(error => {
        console.error(error);
        return null;
      })
    : Promise.resolve(null);

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function buildCacheKey(req: HttpRequest<unknown>): string {
  return req.urlWithParams;
}

async function getFromCache(cacheKey: string): Promise<HttpResponse<unknown> | null> {
  const db = await dbPromise;
  if (!db) {
    return null;
  }

  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const cached = await requestToPromise<CachedResponse | undefined>(store.get(cacheKey));

    if (!cached) {
      return null;
    }

    return new HttpResponse<unknown>({
      body: cached.body,
      status: cached.status,
      statusText: cached.statusText,
      headers: new HttpHeaders(cached.headers)
    });
  } catch (error) {
    console.error(error);
    return null;
  }
}

async function saveToCache(cacheKey: string, response: HttpResponse<unknown>): Promise<void> {
  const db = await dbPromise;
  if (!db) {
    return;
  }

  const headers: Record<string, string[]> = {};
  response.headers.keys().forEach(key => {
    const values = response.headers.getAll(key);
    if (values && values.length) {
      headers[key] = values;
    }
  });

  const serializableResponse: CachedResponse = {
    body: response.body,
    status: response.status,
    statusText: response.statusText,
    headers
  };

  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put(serializableResponse, cacheKey);

      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error);
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error(error);
  }
}

export function devCacheInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  if (!enabled || req.method !== 'GET') {
    return next(req);
  }

  const cacheKey = buildCacheKey(req);
  return from(getFromCache(cacheKey)).pipe(
    switchMap(cachedResponse => {
      if (cachedResponse) {
        return of(cachedResponse);
      }

      return next(req).pipe(
        tap(event => {
          if (event instanceof HttpResponse) {
            void saveToCache(cacheKey, event);
          }
        })
      );
    })
  );
}
